import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useApplications } from '../hooks/useApplications'
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
  Banner,
  ConfirmDialog,
  Table,
  Animate,
  useIsDesktop
} from 'wss3-forge'
import {
  Add20Regular,
  Delete20Regular,
  Edit20Regular
} from '@fluentui/react-icons'
import type { Application } from '../types/database'

export function Applications() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  usePageTitle('Applications')
  const { applications, error, deleteApplication } = useApplications()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedApp, setSelectedApp] = useState<Application | null>(null)

  const handleDeleteClick = (app: Application) => {
    setSelectedApp(app)
    setShowDeleteConfirm(true)
  }

  const handleDelete = async () => {
    if (!selectedApp) return
    await deleteApplication(selectedApp.id)
    setShowDeleteConfirm(false)
    setSelectedApp(null)
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        <Animate type="fadeIn">
          <HStack gap="md" style={{ alignItems: isDesktop ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <Heading level={2}>Applications</Heading>
              <Text color="muted">Gérez vos applications Webba ID</Text>
            </div>
            <Button icon={<Add20Regular />} onClick={() => navigate('/platform/applications/new')} style={{ width: isDesktop ? 'auto' : '100%' }}>
              Nouvelle application
            </Button>
          </HStack>
        </Animate>

        {error && (
          <Banner variant="error" title="Erreur">
            {error}
          </Banner>
        )}

        <Animate type="slideInUp" delay={100}>
          {applications.length === 0 ? (
            <Card padding="xl">
              <VStack gap="md" style={{ alignItems: 'center', textAlign: 'center' }}>
                <Heading level={4}>Aucune application</Heading>
                <Text color="muted">
                  Créez votre première application pour utiliser l'authentification Webba ID.
                </Text>
                <Button icon={<Add20Regular />} onClick={() => navigate('/platform/applications/new')}>
                  Créer une application
                </Button>
              </VStack>
            </Card>
          ) : (
            <Table
            columns={[
              {
                key: 'name',
                header: 'Application',
                render: (_, row: Application) => (
                  <HStack gap="md" style={{ alignItems: 'center' }}>
                    {row.image_url ? (
                      <img
                        src={row.image_url}
                        alt={row.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: 'var(--radius-sm)',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: 'var(--radius-sm)',
                        backgroundColor: row.theme_color || 'var(--brand-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        {row.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <Text weight="medium">{row.name}</Text>
                      {row.description && (
                        <Text size="sm" color="muted">{row.description}</Text>
                      )}
                    </div>
                  </HStack>
                )
              },
              {
                key: 'providers',
                header: 'Fournisseurs',
                render: (_, row: Application) => (
                  <HStack gap="xs" style={{ flexWrap: 'wrap' }}>
                    {(row.allowed_providers || ['email']).map(p => (
                      <Badge key={p} size="sm">{p}</Badge>
                    ))}
                  </HStack>
                )
              },
              {
                key: 'status',
                header: 'Statut',
                render: (_, row: Application) => (
                  <Badge variant={row.is_active ? 'success' : 'default'}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                )
              },
              {
                key: 'actions',
                header: '',
                render: (_, row: Application) => (
                  <HStack gap="xs">
                    <IconButton
                      icon={<Edit20Regular />}
                      size="sm"
                      onClick={() => navigate(`/platform/applications/${row.id}`)}
                    />
                    <IconButton
                      icon={<Delete20Regular />}
                      size="sm"
                      onClick={() => handleDeleteClick(row)}
                    />
                  </HStack>
                )
              }
            ]}
            data={applications}
            onRowClick={(row) => navigate(`/platform/applications/${row.id}`)}
          />
          )}
        </Animate>
      </VStack>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Supprimer l'application"
        description={`Êtes-vous sûr de vouloir supprimer "${selectedApp?.name}" ? Cette action est irréversible et cassera toutes les intégrations existantes.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  )
}
